/**
 * The TipTap schema + markdown round-trip contract for derivative editing.
 *
 * Derivative bodies are stored as markdown and diffed line-by-line as the
 * immutable clinical audit trail (`backend/app/services/documents/diff.py`), so
 * this module's job is not "render markdown" but "round-trip markdown without
 * corrupting the record". Three properties are load-bearing, and each cost a
 * defect found by the round-trip probe before this shipped:
 *
 * 1. **Convergence.** A semantically no-op save must produce an empty diff.
 *    A serializer that reflows on every pass makes a one-word edit look like a
 *    forty-line change, and an unreadable audit trail does not get read.
 * 2. **Citations survive.** ProseMirror escapes `[` because it could open a
 *    link, so a text-encoded marker comes back as `\\[\\[...\\]\\]` and stops
 *    matching the renderer's grammar. All markers (`src`, `inf`, `cite`) are
 *    now modelled as self-serializing atom nodes to prevent this at the source.
 * 3. **Tables survive.** StarterKit ships no table support, so a GFM pipe table
 *    parses as paragraphs and serializes back as one run-on line. On the
 *    teacher-support guide that is the entire clinical-findings table — real
 *    content loss, not formatting.
 *
 * `markdownRoundTrip.test.ts` guards all three against the real exemplar
 * corpus. It is also the loud-failure mechanism for a serializer bump: the
 * `@tiptap/*` packages are pinned to exact versions (they use exact
 * inter-package peers), and any change to their output shows up there rather
 * than silently in a provider's diff view.
 */
import { Editor, mergeAttributes, Node, type Extensions } from "@tiptap/core";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Color } from "@tiptap/extension-color";
import { Heading, type Level } from "@tiptap/extension-heading";
import { Highlight } from "@tiptap/extension-highlight";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import { Text } from "@tiptap/extension-text";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { FontFamily } from "@tiptap/extension-font-family";
import { TextAlign } from "@tiptap/extension-text-align";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";

import {
  HEADING_STYLES,
  LIST_CLASSES,
  PARAGRAPH_CLASS,
  TABLE_CLASSES,
} from "../blueprint/proseStyles";

/**
 * Heading, styled from the same {@link HEADING_STYLES} the read-only
 * `Markdown` renderer uses, so a section heading looks identical whether the
 * provider is in Rich or the split-view preview. `@tailwindcss/typography`
 * is not installed, so the `prose` classes this editor used to carry were
 * inert — this is the fix, not a workaround.
 */
const StyledHeading = Heading.extend({
  renderHTML({ node, HTMLAttributes }) {
    const level = node.attrs.level as Level;
    const tag = `h${level}` as "h1" | "h2" | "h3";
    return [
      tag,
      mergeAttributes(HTMLAttributes, { class: HEADING_STYLES[tag] }),
      0,
    ];
  },
});

/** The shape `tiptap-markdown` adds to `editor.storage` (it ships no types for it). */
interface MarkdownStorage {
  markdown: { getMarkdown: () => string };
}

/**
 * A text node that does not HTML-escape its content when serializing.
 *
 * `tiptap-markdown` hard-codes `state.text(escapeHTML(node.text))` in its text
 * serializer with no option to disable it, so `<1 percentile` is emitted as
 * `&lt;1 percentile`. That is defensible for an editor whose markdown may be
 * rendered as HTML; it is wrong here. Our renderer (`blueprint/Markdown.tsx`)
 * builds React elements and never sets `innerHTML`, and the `.md` export is
 * handed to a parent verbatim — so the entity is pure corruption of clinical
 * notation (percentiles, ranges, comparisons) with no safety benefit.
 *
 * Overriding only the text node keeps ProseMirror's own markdown escaping
 * (`*`, `[`, `_`) intact; it drops only the HTML entity pass.
 */
export const PlainText = Text.extend({
  addStorage() {
    return {
      markdown: {
        serialize(
          state: { text: (value: string) => void },
          node: { text?: string },
        ) {
          state.text(node.text ?? "");
        },
        parse: {},
      },
    };
  },
});

/** The inline citation grammar, anchored so it only matches at the cursor. */
const CITATION_AT_POS = /^\[\[src:([^\]]+)\]\]/;

/** The inference grammar. `*` not `+`: an unattributed `[[inf:]]` is valid. */
const INFERENCE_AT_POS = /^\[\[inf:([^\]]*)\]\]/;

/**
 * The unresolved-citation grammar — an imported profile's `[[cite:...]]` marker
 * (`profile_import.parser.rewrite_citations`, ADR-058). `*` not `+` mirrors the
 * inference grammar; the comma-joined tag text is never validated here.
 */
const CITE_AT_POS = /^\[\[cite:([^\]]*)\]\]/;

/** The minimal markdown-it surface the citation rule needs. */
interface InlineState {
  src: string;
  pos: number;
  push: (type: string, tag: string, nesting: number) => { meta: unknown };
}
interface MarkdownIt {
  inline: {
    ruler: {
      before: (
        beforeName: string,
        name: string,
        rule: (state: InlineState, silent: boolean) => boolean,
      ) => void;
    };
  };
  renderer: {
    rules: Record<string, (tokens: { meta: unknown }[], idx: number) => string>;
  };
}

/**
 * Escape a citation ref for safe interpolation into an HTML attribute.
 *
 * Refs are minted by the backend from a closed grammar, but this parser also
 * sees provider-typed text, so the value is treated as untrusted.
 *
 * @param value - The raw ref.
 * @returns The value with HTML-significant characters escaped.
 */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Teach markdown-it to emit an element for every PCP inline marker grammar —
 * `[[src:...]]`, `[[inf:...]]`, and `[[cite:...]]`.
 *
 * Registering all three here (rather than one setup per node) is why
 * {@link InferenceNode} and {@link UnresolvedCitationNode} carry an empty
 * `parse` spec: {@link CitationNode}'s `parse.setup` runs this once and teaches
 * markdown-it every marker at the same time.
 *
 * @param markdownit - The parser instance to extend.
 */
export function citationMarkdownPlugin(markdownit: MarkdownIt): void {
  markerRule(markdownit, "citation", CITATION_AT_POS, "data-citation-ref");
  markerRule(markdownit, "inference", INFERENCE_AT_POS, "data-inference-cards");
  markerRule(
    markdownit,
    "unresolvedCitation",
    CITE_AT_POS,
    "data-unresolved-citation",
  );
}

/**
 * Register one inline marker grammar with markdown-it.
 *
 * Citations and inferences differ only in prefix and attribute, so they share
 * one rule builder — keeping their parsing from drifting apart, which is how a
 * marker grammar quietly stops matching.
 *
 * @param markdownit - The parser to extend.
 * @param name - The token/rule name.
 * @param pattern - The anchored grammar for this marker.
 * @param attribute - The data attribute the emitted element carries.
 */
function markerRule(
  markdownit: MarkdownIt,
  name: string,
  pattern: RegExp,
  attribute: string,
): void {
  markdownit.inline.ruler.before("link", name, (state, silent) => {
    const match = pattern.exec(state.src.slice(state.pos));
    if (match === null) return false;
    if (!silent) {
      state.push(name, "span", 0).meta = { value: match[1] };
    }
    state.pos += match[0].length;
    return true;
  });
  markdownit.renderer.rules[name] = (tokens, idx) => {
    const { value } = tokens[idx].meta as { value: string };
    return `<span ${attribute}="${escapeAttribute(value)}"></span>`;
  };
}

/**
 * Post-process HTML generated from markdown before ProseMirror DOMParser parses it.
 *
 * Ensures `</strong>\n` is replaced with `</strong> ` so ProseMirror's DOMParser does not
 * collapse single newlines following bold elements into zero space.
 *
 * @param html - HTML string rendered from markdown.
 * @returns Hardened HTML with trailing newline converted to space.
 */
export function markdownToHtml(html: string): string {
  return html.replace(/<\/strong>\n/g, "</strong> ");
}

/**
 * The inline citation node — a `[[src:...]]` marker as a first-class atom.
 *
 * Modelling a citation as a node rather than as text is what makes the rich
 * editor possible at all: it gives the marker a node view (the dagger, its
 * tooltip and its excerpt popover) and, just as importantly, it serializes
 * itself. ProseMirror escapes `[` in plain text, so a text-encoded marker comes
 * back as `\[\[src:...\]\]` and silently stops resolving; an atom writes its
 * own unescaped markdown and cannot be corrupted that way.
 *
 * The node is deliberately defined here without a node view. The schema and the
 * markdown contract are headless and fully covered; the React node view is
 * attached in `TipTapEditor.tsx`, which carries the DOM-mounting coverage
 * exclude.
 */
export const CitationNode = Node.create({
  name: "citation",
  inline: true,
  group: "inline",
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      ref: {
        default: "",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-citation-ref") ?? "",
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-citation-ref": attributes.ref,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-citation-ref]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes];
  },

  addStorage() {
    return {
      markdown: {
        serialize(
          state: { text: (value: string, escape?: boolean) => void },
          node: { attrs: { ref: string } },
        ) {
          // `escape: false` is the point of the node — the marker must survive
          // byte-exact for the renderer's grammar to match it.
          state.text(`[[src:${node.attrs.ref}]]`, false);
        },
        parse: {
          setup(markdownit: MarkdownIt) {
            citationMarkdownPlugin(markdownit);
          },
          updateDOM(element: HTMLElement) {
            element.innerHTML = markdownToHtml(element.innerHTML);
          },
        },
      },
    };
  },
});

/**
 * The inline inference node — a `[[inf:...]]` marker as a first-class atom.
 *
 * Same reasoning as {@link CitationNode}: as an atom it serializes itself
 * unescaped and can carry a node view. It is a separate node rather than a
 * variant of the citation because it asserts something different — not "a
 * source proves this" but "this was reasoned from these cards" — and the
 * citation-validation machinery must not try to resolve it as a source span.
 */
export const InferenceNode = Node.create({
  name: "inference",
  inline: true,
  group: "inline",
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      cards: {
        default: "",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-inference-cards") ?? "",
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-inference-cards": attributes.cards,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-inference-cards]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes];
  },

  addStorage() {
    return {
      markdown: {
        serialize(
          state: { text: (value: string, escape?: boolean) => void },
          node: { attrs: { cards: string } },
        ) {
          state.text(`[[inf:${node.attrs.cards}]]`, false);
        },
        parse: {},
      },
    };
  },
});

/**
 * The inline unresolved-citation node — a `[[cite:...]]` marker as a first-class atom.
 *
 * Same reasoning as {@link CitationNode}/{@link InferenceNode}: an imported
 * profile's citation tags become a `[[cite:<tags>]]` marker
 * (`profile_import.parser.rewrite_citations`, ADR-058) that resolves to no PCP
 * source in Phase 1. Modelling it as a self-serializing atom is what keeps it
 * intact — without a node it was parsed as ordinary text and the ProseMirror
 * serializer escaped its brackets to `\[\[cite:...\]\]`, silently breaking the
 * dagger grammar for every profile edited in the rich editor. It is a separate
 * node from the citation because it asserts an *unresolved* reference — a
 * text-only dagger, never a link — which the citation-validation machinery must
 * not try to resolve as a source span.
 *
 * Like {@link InferenceNode}, its `parse` is empty: {@link CitationNode}'s
 * `parse.setup` registers this marker's markdown-it rule via
 * {@link citationMarkdownPlugin}. The React node view (the dagger) is attached
 * in `TipTapEditor.tsx`, which carries the DOM-mounting coverage exclude.
 */
export const UnresolvedCitationNode = Node.create({
  name: "unresolvedCitation",
  inline: true,
  group: "inline",
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      tags: {
        default: "",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-unresolved-citation") ?? "",
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-unresolved-citation": attributes.tags,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-unresolved-citation]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes];
  },

  addStorage() {
    return {
      markdown: {
        serialize(
          state: { text: (value: string, escape?: boolean) => void },
          node: { attrs: { tags: string } },
        ) {
          // `escape: false` is the whole point — the marker must survive
          // byte-exact so the renderer's `[[cite:...]]` grammar (and its dagger)
          // still match it after an edit round-trip.
          state.text(`[[cite:${node.attrs.tags}]]`, false);
        },
        parse: {},
      },
    };
  },
});

/**
 * Extended TextStyle that supports fontSize attribute rendering as style="font-size: ${size}px".
 */
export const FontSizeTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const size = element.style.fontSize;
          return size ? parseInt(size, 10) : null;
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}px` };
        },
      },
    };
  },
});

/**
 * The extension set every derivative editor and the round-trip guard share.
 *
 * @returns The configured TipTap extensions, in schema order.
 */
export function derivativeExtensions(): Extensions {
  return [
    StarterKit.configure({
      text: false,
      heading: false,
      paragraph: false,
      bulletList: false,
      orderedList: false,
      underline: false,
    }),
    StyledHeading.configure({ levels: [1, 2, 3] }),
    Paragraph.configure({ HTMLAttributes: { class: PARAGRAPH_CLASS } }),
    BulletList.configure({ HTMLAttributes: { class: LIST_CLASSES.ul } }),
    OrderedList.configure({ HTMLAttributes: { class: LIST_CLASSES.ol } }),
    PlainText,
    CitationNode,
    InferenceNode,
    UnresolvedCitationNode,
    Table.configure({
      resizable: false,
      HTMLAttributes: { class: TABLE_CLASSES.table },
    }),
    TableRow,
    TableHeader.configure({
      HTMLAttributes: { class: TABLE_CLASSES.th },
    }),
    TableCell.configure({
      HTMLAttributes: { class: TABLE_CLASSES.td },
    }),
    Underline,
    Highlight.configure({ multicolor: true }),
    FontSizeTextStyle,
    Color,
    FontFamily,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Markdown.configure({ html: true, breaks: false }),
  ];
}

/**
 * Restore `[[src:...]]`, `[[inf:...]]`, and `[[cite:...]]` markers the ProseMirror serializer escaped.
 *
 * Legacy backstop: `[[src:]]` and `[[inf:]]` have self-serializing nodes
 * ({@link CitationNode}, {@link InferenceNode}) that prevent escaping at the source,
 * but this remains as a safety net for any marker that bypasses the node parse.
 *
 * @param markdown - Serializer output.
 * @returns The same markdown with citation, inference, and cite markers restored.
 */
export function unescapeCitations(markdown: string): string {
  return markdown.replace(/\\\[\\\[(src|inf|cite):(.+?)\\\]\\\]/g, "[[$1:$2]]");
}

/**
 * Read an editor's content back as markdown, with citations intact.
 *
 * @param editor - The TipTap editor instance.
 * @returns The document as markdown.
 */
export function readMarkdown(editor: Editor): string {
  const storage = editor.storage as unknown as MarkdownStorage;
  return unescapeCitations(storage.markdown.getMarkdown());
}

/**
 * Round-trip a markdown string through the derivative schema.
 *
 * Used by the round-trip guard and by any caller that needs to canonicalize a
 * body without mounting an editor (the editor is headless here — no DOM element
 * is attached).
 *
 * @param markdown - The markdown to parse and re-serialize.
 * @returns The canonical markdown for that content.
 */
export function canonicalizeMarkdown(markdown: string): string {
  const editor = new Editor({
    extensions: derivativeExtensions(),
    content: markdown,
  });
  try {
    return readMarkdown(editor);
  } finally {
    editor.destroy();
  }
}
